import "./ReportThirdSection.css";
import  { useEffect, useRef ,useContext} from "react";
import { ThemeContext } from "../context/themeContext";

// KEY OBSERVATIONS
const remarksData = [
  { high: "Clearly defined, high-intensity pain point with strong user empathy.", medium: "Problem identified, but needs direct customer research.", low: "Vague or low-stakes problem statement." },
  { high: "Uniquely differentiated solution with technical feasibility and a clear moat.", medium: "Clear value prop, but differentiation could be stronger.", low: "High replication risk or unclear differentiation." },
  { high: "Large target addressable market with high macro tailwinds and competitive realism.", medium: "Market is viable, but competition is intense.", low: "Saturated market or lack of competitor awareness." },
  { high: "Excellent domain alignment, technical capabilities, and execution track record.", medium: "Good founder alignment; team gaps exist in key areas.", low: "Weak team alignment or solo founder execution risk." },
  { high: "Realistic revenue model, strong pricing strategy, and solid unit economics pathway.", medium: "Plausible business model, but high dependency on single stream.", low: "Unclear monetization or weak unit economic math." },
  { high: "Specific, targeted acquisition channels and proven early traction signals.", medium: "Acquisition channels named, but traction is pre-pilot.", low: "No traction signals or generic acquisition channels." },
  { high: "Strong regulatory compliance, robust IP strategy, and clear risk mitigation.", medium: "Aware of regulations; minor compliance gaps exist.", low: "High regulatory risk or no compliance prep." }
];

const getColorGradeObservation = (score, max, index) => {
  const percentage = (score / max) * 100;

  if (percentage >= 90)
    return { color: "#3DA935", colorName: "Green", grade: "Distinction", observation: remarksData[index].high };

  if (percentage >= 75)
    return { color: "#F6C63A", colorName: "Yellow", grade: "Positive", observation: remarksData[index].medium };

  if (percentage >= 60)
    return { color: "#F7931E", colorName: "Orange", grade: "Neutral", observation: remarksData[index].low };

  return { color: "#E74C3C", colorName: "Red", grade: "Negative", observation: remarksData[index].low };
};

const CanvasCircle = ({ score, max, color,k }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const size = 40;
    const lineWidth = 5;

    canvas.width = size;
    canvas.height = size;

    const percentage = score / max;
    const endAngle = percentage * 2 * Math.PI;

    // background circle
    ctx.beginPath();
    ctx.strokeStyle = color + "33";
    ctx.lineWidth = lineWidth;
    ctx.arc(size / 2, size / 2, size / 2 - lineWidth, 0, 2 * Math.PI);
    ctx.stroke();

    // foreground arc
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";
    ctx.arc(size / 2, size / 2, size / 2 - lineWidth, -Math.PI / 2, endAngle - Math.PI / 2);
    ctx.stroke();

    // text
    ctx.fillStyle = k;
    ctx.font = "bold 12px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(score, size / 2, size / 2);
  }, [score, max, color,k]);

  return <canvas ref={canvasRef} style={{ display: "block" }} />;
};

const ReportThirdSection = ({res}) => {
    //const res=JSON.parse(localStorage.getItem("scorecardData"))
    const {activeTheme}=useContext(ThemeContext)
    let k='#000'
if(activeTheme==="dark"){
    k='#0b0b0bff'
}
  // mapping backend sectionScores to modules
  const modules = [
    { key: "customerEmpathy", label: "Customer Empathy & Problem Validation", max: 100 },
    { key: "solutionViability", label: "Solution Viability & Differentiation", max: 90 },
    { key: "marketOpportunity", label: "Market Opportunity & Timing", max: 80 },
    { key: "founderReadiness", label: "Founder Readiness & Execution Capability", max: 100 },
    { key: "businessModel", label: "Business Model & Unit Economics", max: 80 },
    { key: "goToMarket", label: "Go-To-Market & Traction Signals", max: 90 },
    { key: "riskCompliance", label: "Risk Management & Regulatory Compliance", max: 60 }
  ];

  const data = modules.map((mod) => ({
    module: mod.label,
    score: res?.sectionScores?.[mod.key] ?? 0,
    max: mod.max
  }));

  return (
    <div   className={`report-third-section ${activeTheme === "dark" ? "dark-mode" : ""}`}>
      <div className="table-container">
        <h2 className="table-heading">Marsmate Idea Validation Summary</h2>

        <div className="table-scroll-wrapper">
          <table className="score-table">
            <thead className="table-header">
              <tr>
                <th>S.No</th>
                <th>Module</th>
                <th>Score</th>
                <th>Max. Pts</th>
                <th>Grade</th>
                <th>Key Observation</th>
              </tr>
            </thead>

            <tbody>
              {data.map((row, index) => {
                const { color, colorName, grade, observation } =
                  getColorGradeObservation(row.score, row.max, index);

                const aiObservation = res?.sectionScores?.aiReport?.moduleObservations?.[modules[index].key] ||
                                      res?.sectionScores?.ai_report?.moduleObservations?.[modules[index].key] ||
                                      res?.aiReport?.moduleObservations?.[modules[index].key];
                const displayObservation = aiObservation || observation;

                // Detect dark mode to re-render canvas
                const isDarkMode = document.body.classList.contains("dark-mode");

                return (
                  <tr key={index}>
                    <td style={{ textAlign: "center" }}>{index + 1}</td>
                    <td>{row.module}</td>

                    <td>
                      <CanvasCircle
                        key={isDarkMode} // 🔁 Re-render canvas when theme changes
                        score={row.score}
                        max={row.max}
                        color={color}
                        k={k}
                      />
                    </td>

                    <td style={{ textAlign: "center" }}>{row.max}</td>

                    <td>
                      <div className="grade-column" style={{ color }}>
                        <strong style={{ paddingRight: "7px" }}>{colorName}</strong>
                        <span style={{ color: "black" }}>{grade}</span>
                      </div>
                    </td>

                    <td>{displayObservation}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportThirdSection;
